import { AsyncPipe } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { Appointment, AppointmentPhoto } from '../../models/appointment.model';
import { AppointmentApiService } from '../../services/appointment-api.service';
import { AppointmentActions } from '../../store/appointments/appointment.actions';
import {
  selectAppointmentError,
  selectAppointmentsLoading,
  selectCalendarAppointments,
  selectPendingAppointments,
} from '../../store/appointments/appointment.selectors';

interface AdminCalendarDay {
  dateIso: string;
  weekday: string;
  day: string;
  month: string;
  inMonth: boolean;
  past: boolean;
  slots: Array<{
    label: string;
    status: 'open' | 'pending' | 'booked' | 'closed';
    appointment?: Appointment;
  }>;
}

@Component({
  selector: 'atr-admin',
  standalone: true,
  imports: [AsyncPipe, ReactiveFormsModule],
  template: `
    <main class="admin-page page-shell">
      <section class="page-hero compact">
        <p class="section-kicker">Admin</p>
        <h1>Appointment review and calendar.</h1>
        <p>
          New requests land as pending. Accept one to move it into the booked calendar, or decline it when the slot does not work.
        </p>
      </section>

      @if (!unlocked) {
        <section class="admin-lock section">
          <div>
            <p class="section-kicker">Locked</p>
            <h2>Enter your admin PIN.</h2>
          </div>
          <form class="booking-form compact-form" [formGroup]="loginForm" (ngSubmit)="unlock()">
            <label class="full">
              Admin PIN
              <input formControlName="pin" type="password" autocomplete="current-password" />
            </label>
            @if (pinError) {
              <p class="form-status error full">That PIN did not match.</p>
            }
            <button class="button primary full" type="submit">Open admin</button>
          </form>
        </section>
      } @else {
        <section class="admin-grid section">
          <div class="admin-panel">
            <div class="panel-heading">
              <p class="section-kicker">Pending requests</p>
              <button class="button ghost small" type="button" (click)="reload()">Refresh</button>
            </div>
            @if (loading$ | async) {
              <p class="muted">Loading appointments...</p>
            }
            @if (error$ | async; as error) {
              <p class="form-status error">{{ error }}</p>
            }
            <div class="appointment-list">
              @for (appointment of pending$ | async; track appointment._id) {
                <article class="appointment-card pending">
                  <div>
                    <span>{{ appointment.service }}</span>
                    <h3>{{ appointment.name }}</h3>
                    <p>{{ appointment.vehicle || 'Vehicle not listed' }}</p>
                    <p>{{ appointment.email || appointment.contact }}</p>
                    @if (appointment.phone) {
                      <p>{{ appointment.phone }}</p>
                    }
                    @if (appointment.addOns?.length) {
                      <p><strong>Extras:</strong> {{ appointment.addOns?.join(', ') }}</p>
                    }
                    @if (appointment.mobileDetail) {
                      <p>Mobile detail requested{{ appointment.mobileAddress ? ': ' + appointment.mobileAddress : '' }}</p>
                      <p>{{ appointment.waterElectricityAvailable ? 'Water and electricity available' : 'Water/electricity not confirmed' }}</p>
                    }
                    <p>{{ appointment.message || 'No extra notes.' }}</p>
                    @if (appointment.photos?.length) {
                      <div class="admin-photos">
                        <span>Vehicle photos</span>
                        <div class="admin-photo-grid">
                          @for (photo of appointment.photos; track photo.filename; let photoIndex = $index) {
                            <figure>
                              <button class="image-zoom admin-photo-button" type="button" (click)="openPhotoViewer(appointment._id, appointment.photos, photoIndex)" [attr.aria-label]="'Open ' + photo.originalName + ' larger'">
                                <img [src]="photoUrl(appointment._id, photo.filename)" [alt]="photo.originalName" />
                              </button>
                              <button class="button ghost small" type="button" (click)="removePhoto(appointment._id, photo.filename)">Remove</button>
                            </figure>
                          }
                        </div>
                      </div>
                    }
                  </div>
                  <div class="admin-fields">
                    <label>
                      Schedule start
                      <input #start type="datetime-local" [value]="toLocalInput(appointment.preferredDate, appointment.preferredTime)" required />
                    </label>
                    <label>
                      End
                      <input #end type="datetime-local" />
                    </label>
                    <label>
                      Notes
                      <textarea #notes rows="3"></textarea>
                    </label>
                    @if (scheduleError) {
                      <p class="form-status error">{{ scheduleError }}</p>
                    }
                    <div class="admin-actions">
                      <button class="button primary" type="button" (click)="book(appointment, start.value, end.value, notes.value)">Accept</button>
                      <button class="button ghost" type="button" (click)="decline(appointment, notes.value)">Decline</button>
                    </div>
                  </div>
                </article>
              } @empty {
                <p class="muted">No pending appointment requests.</p>
              }
            </div>
          </div>

        </section>

        <section class="availability admin-month section">
          <div class="section-heading">
            <p class="section-kicker">Schedule</p>
            <h2>{{ calendarTitle }} calendar.</h2>
            <p>Pending requests and booked appointments show here in the same month view customers see.</p>
          </div>
          <div class="calendar-toolbar" aria-label="Calendar month controls">
            <button class="button ghost small" type="button" (click)="previousMonth()" [disabled]="isCurrentMonth()">Previous</button>
            <strong>{{ calendarTitle }}</strong>
            <button class="button ghost small" type="button" (click)="nextMonth()">Next</button>
          </div>
          <div class="availability-grid">
            @for (weekday of weekdays; track weekday) {
              <div class="availability-weekday">{{ weekday }}</div>
            }
            @for (day of calendarDays; track day.dateIso) {
              <article class="availability-day admin-schedule-day" [class.outside-month]="!day.inMonth" [class.past]="day.past">
                <div class="availability-date">
                  <span>{{ day.month }}</span>
                  <strong>{{ day.day }}</strong>
                  <em>{{ day.weekday }}</em>
                </div>
                <div class="availability-slots">
                  @for (slot of day.slots; track slot.label) {
                    @if (slot.appointment) {
                      <button
                        class="slot-action"
                        type="button"
                        [class.pending]="slot.status === 'pending'"
                        [class.booked]="slot.status === 'booked'"
                        (click)="openCancelDialog(slot.appointment)"
                      >
                        {{ slot.label }}
                      </button>
                    } @else {
                      <span [class]="slot.status">{{ slot.label }}</span>
                    }
                  }
                </div>
              </article>
            }
          </div>
        </section>

        @if (appointmentToCancel) {
          <div class="admin-dialog-backdrop" role="dialog" aria-modal="true" aria-label="Booked appointment details" (click)="closeCancelDialog()">
            <div class="admin-dialog" (click)="$event.stopPropagation()">
              <p class="section-kicker">{{ appointmentToCancel.status === 'pending' ? 'Pending appointment' : 'Booked appointment' }}</p>
              <h2>{{ appointmentToCancel.name }}</h2>
              <div class="appointment-detail-grid">
                <div>
                  <span>Service</span>
                  <strong>{{ appointmentToCancel.service }}</strong>
                </div>
                <div>
                  <span>Scheduled</span>
                  <strong>{{ displayScheduledTime(appointmentToCancel) }}</strong>
                </div>
                <div>
                  <span>Requested slot</span>
                  <strong>{{ appointmentToCancel.preferredTime || 'Not listed' }}</strong>
                </div>
                <div>
                  <span>Contact</span>
                  <strong>{{ appointmentToCancel.email || appointmentToCancel.contact }}</strong>
                </div>
                @if (appointmentToCancel.phone) {
                  <div>
                    <span>Phone</span>
                    <strong>{{ appointmentToCancel.phone }}</strong>
                  </div>
                }
                <div>
                  <span>Vehicle</span>
                  <strong>{{ appointmentToCancel.vehicle || 'Vehicle not listed' }}</strong>
                </div>
                @if (appointmentToCancel.addOns?.length) {
                  <div>
                    <span>Add-ons</span>
                    <strong>{{ appointmentToCancel.addOns?.join(', ') }}</strong>
                  </div>
                }
                @if (appointmentToCancel.mobileDetail) {
                  <div class="wide">
                    <span>Mobile detail</span>
                    <strong>
                      {{ appointmentToCancel.mobileAddress || 'Address not listed' }} -
                      {{ appointmentToCancel.waterElectricityAvailable ? 'Water and electricity available' : 'Water/electricity not confirmed' }}
                    </strong>
                  </div>
                }
                <div class="wide">
                  <span>Customer notes</span>
                  <strong>{{ appointmentToCancel.message || 'No customer notes.' }}</strong>
                </div>
                @if (appointmentToCancel.photos?.length) {
                  <div class="wide admin-photos">
                    <span>Vehicle photos</span>
                    <div class="admin-photo-grid">
                      @for (photo of appointmentToCancel.photos; track photo.filename; let photoIndex = $index) {
                        <figure>
                          <button class="image-zoom admin-photo-button" type="button" (click)="openPhotoViewer(appointmentToCancel._id, appointmentToCancel.photos, photoIndex)" [attr.aria-label]="'Open ' + photo.originalName + ' larger'">
                            <img [src]="photoUrl(appointmentToCancel._id, photo.filename)" [alt]="photo.originalName" />
                          </button>
                          <button class="button ghost small" type="button" (click)="removePhoto(appointmentToCancel._id, photo.filename)">Remove</button>
                        </figure>
                      }
                    </div>
                  </div>
                }
                @if (appointmentToCancel.adminNotes) {
                  <div class="wide">
                    <span>Admin notes</span>
                    <strong>{{ appointmentToCancel.adminNotes }}</strong>
                  </div>
                }
              </div>
              <div class="admin-actions">
                <button class="button ghost" type="button" (click)="closeCancelDialog()">Close</button>
                @if (appointmentToCancel.status === 'booked' && !isPastAppointment(appointmentToCancel)) {
                  <button class="button primary" type="button" (click)="confirmCancelBooking()">Cancel and open slot</button>
                }
                @if (appointmentToCancel.status === 'booked' && isPastAppointment(appointmentToCancel)) {
                  <button class="button ghost" type="button" (click)="deletePastAppointment()">Delete appointment</button>
                }
              </div>
            </div>
          </div>
        }
        @if (selectedPhoto) {
          <div class="lightbox" role="dialog" aria-modal="true" aria-label="Expanded vehicle photo" (click)="closePhotoViewer()">
            <button class="lightbox-close" type="button" (click)="closePhotoViewer()" aria-label="Close expanded photo">Close</button>
            @if (photoViewerPhotos.length > 1) {
              <button class="lightbox-nav lightbox-prev" type="button" (click)="previousPhoto($event)" aria-label="Previous photo">‹</button>
              <button class="lightbox-nav lightbox-next" type="button" (click)="nextPhoto($event)" aria-label="Next photo">›</button>
            }
            <img [src]="photoUrl(photoViewerAppointmentId, selectedPhoto.filename)" [alt]="selectedPhoto.originalName" (click)="$event.stopPropagation()" />
          </div>
        }
      }
    </main>
  `,
})
export class AdminComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly api = inject(AppointmentApiService);
  private readonly subscription = new Subscription();
  protected readonly pending$ = this.store.select(selectPendingAppointments);
  protected readonly calendar$ = this.store.select(selectCalendarAppointments);
  protected readonly loading$ = this.store.select(selectAppointmentsLoading);
  protected readonly error$ = this.store.select(selectAppointmentError);
  protected readonly loginForm = this.fb.nonNullable.group({
    pin: [''],
  });
  protected unlocked = sessionStorage.getItem('atr-admin') === 'true' && Boolean(sessionStorage.getItem('atr-admin-token'));
  protected pinError = false;
  protected scheduleError = '';
  protected selectedPhoto: AppointmentPhoto | null = null;
  protected photoViewerPhotos: AppointmentPhoto[] = [];
  protected photoViewerAppointmentId = '';
  protected photoViewerIndex = 0;
  protected readonly weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  protected calendarDays: AdminCalendarDay[] = [];
  protected appointmentToCancel: Appointment | null = null;
  protected calendarViewDate = new Date();
  protected calendarTitle = this.calendarViewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  private appointments: Appointment[] = [];

  ngOnInit(): void {
    this.subscription.add(
      this.calendar$.subscribe((appointments) => {
        this.appointments = appointments;
        this.calendarDays = this.buildCalendarDays();
      }),
    );
    this.subscription.add(
      this.error$.subscribe((error) => {
        if (error === 'Admin authentication required. Please enter your PIN again.') {
          this.api.clearAdminSession();
          this.unlocked = false;
          this.pinError = false;
        }
      }),
    );

    if (this.unlocked) {
      this.reload();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  protected unlock(): void {
    this.api.loginAdmin(this.loginForm.controls.pin.value).subscribe({
      next: ({ token }) => {
        this.api.setAdminToken(token);
        this.unlocked = true;
        this.pinError = false;
        sessionStorage.setItem('atr-admin', 'true');
        this.reload();
      },
      error: () => {
        this.pinError = true;
      },
    });
  }

  protected reload(): void {
    this.store.dispatch(AppointmentActions.loadAppointments({ admin: true }));
  }

  protected book(appointment: Appointment, scheduledStart: string, scheduledEnd: string, adminNotes: string): void {
    if (!scheduledStart) {
      this.scheduleError = 'Choose a schedule start before accepting this request.';
      return;
    }

    this.scheduleError = '';
    this.store.dispatch(
      AppointmentActions.updateAppointment({
        id: appointment._id,
        update: {
          status: 'booked',
          scheduledStart: scheduledStart ? new Date(scheduledStart).toISOString() : undefined,
          scheduledEnd: scheduledEnd ? new Date(scheduledEnd).toISOString() : undefined,
          adminNotes,
        },
      }),
    );
  }

  protected decline(appointment: Appointment, adminNotes: string): void {
    this.store.dispatch(
      AppointmentActions.updateAppointment({
        id: appointment._id,
        update: {
          status: 'declined',
          adminNotes,
        },
      }),
    );
  }

  protected photoUrl(appointmentId: string, filename: string): string {
    return this.api.photoUrl(appointmentId, filename);
  }

  protected removePhoto(appointmentId: string, filename: string): void {
    this.api.deletePhoto(appointmentId, filename).subscribe(() => this.reload());
  }

  protected openPhotoViewer(appointmentId: string, photos: AppointmentPhoto[] | undefined, index: number): void {
    this.photoViewerAppointmentId = appointmentId;
    this.photoViewerPhotos = photos || [];
    this.photoViewerIndex = index;
    this.selectedPhoto = this.photoViewerPhotos[index] || null;
    document.body.classList.add('lightbox-open');
  }

  protected closePhotoViewer(): void {
    this.selectedPhoto = null;
    this.photoViewerPhotos = [];
    document.body.classList.remove('lightbox-open');
  }

  protected previousPhoto(event: Event): void {
    event.stopPropagation();
    if (!this.photoViewerPhotos.length) return;
    this.photoViewerIndex = (this.photoViewerIndex - 1 + this.photoViewerPhotos.length) % this.photoViewerPhotos.length;
    this.selectedPhoto = this.photoViewerPhotos[this.photoViewerIndex];
  }

  protected nextPhoto(event: Event): void {
    event.stopPropagation();
    if (!this.photoViewerPhotos.length) return;
    this.photoViewerIndex = (this.photoViewerIndex + 1) % this.photoViewerPhotos.length;
    this.selectedPhoto = this.photoViewerPhotos[this.photoViewerIndex];
  }

  @HostListener('window:keydown.escape')
  protected onPhotoEscape(): void { if (this.selectedPhoto) this.closePhotoViewer(); }

  @HostListener('window:keydown.arrowleft')
  protected onPhotoLeft(): void { if (this.selectedPhoto) this.previousPhoto(new Event('keydown')); }

  @HostListener('window:keydown.arrowright')
  protected onPhotoRight(): void { if (this.selectedPhoto) this.nextPhoto(new Event('keydown')); }

  protected openCancelDialog(appointment: Appointment): void {
    if (appointment.status !== 'pending' && appointment.status !== 'booked') {
      return;
    }

    this.appointmentToCancel = appointment;
  }

  protected closeCancelDialog(): void {
    this.appointmentToCancel = null;
  }

  protected confirmCancelBooking(): void {
    if (!this.appointmentToCancel) {
      return;
    }

    this.store.dispatch(
      AppointmentActions.updateAppointment({
        id: this.appointmentToCancel._id,
        update: {
          status: 'declined',
          adminNotes: 'Cancelled from admin to reopen the slot.',
        },
      }),
    );
    this.closeCancelDialog();
  }

  protected isPastAppointment(appointment: Appointment): boolean {
    if (!appointment.scheduledStart) return false;
    return new Date(appointment.scheduledEnd || appointment.scheduledStart).getTime() < Date.now();
  }

  protected deletePastAppointment(): void {
    if (!this.appointmentToCancel || !this.isPastAppointment(this.appointmentToCancel)) return;
    if (!window.confirm('Delete this past appointment and its uploaded photos?')) return;

    this.api.deleteAppointment(this.appointmentToCancel._id).subscribe(() => {
      this.closeCancelDialog();
      this.reload();
    });
  }

  protected toLocalInput(date?: string, time?: string): string {
    if (!date) {
      return '';
    }

    return `${date}T${this.timeForSlot(time)}`;
  }

  protected displayScheduledTime(appointment: Appointment): string {
    if (appointment.scheduledStart) {
      const start = new Date(appointment.scheduledStart);
      const end = appointment.scheduledEnd ? new Date(appointment.scheduledEnd) : undefined;
      const date = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      const startTime = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

      if (end) {
        const endTime = end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
        return `${date}, ${startTime} - ${endTime}`;
      }

      return `${date}, ${startTime}`;
    }

    if (appointment.preferredDate) {
      const date = new Date(`${appointment.preferredDate}T12:00:00`).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return appointment.preferredTime ? `${date}, ${appointment.preferredTime}` : date;
    }

    return appointment.preferredTime || 'Time not listed';
  }

  protected previousMonth(): void {
    if (this.isCurrentMonth()) {
      return;
    }

    this.calendarViewDate = new Date(this.calendarViewDate.getFullYear(), this.calendarViewDate.getMonth() - 1, 1);
    this.refreshCalendarTitle();
    this.calendarDays = this.buildCalendarDays();
  }

  protected nextMonth(): void {
    this.calendarViewDate = new Date(this.calendarViewDate.getFullYear(), this.calendarViewDate.getMonth() + 1, 1);
    this.refreshCalendarTitle();
    this.calendarDays = this.buildCalendarDays();
  }

  protected isCurrentMonth(): boolean {
    const today = new Date();
    return this.calendarViewDate.getFullYear() === today.getFullYear() && this.calendarViewDate.getMonth() === today.getMonth();
  }

  private timeForSlot(slot?: string): string {
    switch (slot) {
      case 'Afternoon':
        return '13:00';
      case 'Evening':
        return '18:00';
      case 'Morning':
      case 'All day':
      default:
        return '09:00';
    }
  }

  private buildCalendarDays(): AdminCalendarDay[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const monthStart = new Date(this.calendarViewDate.getFullYear(), this.calendarViewDate.getMonth(), 1);
    const calendarStart = new Date(monthStart);
    calendarStart.setDate(monthStart.getDate() - monthStart.getDay());
    const monthEnd = new Date(this.calendarViewDate.getFullYear(), this.calendarViewDate.getMonth() + 1, 0);
    const calendarEnd = new Date(monthEnd);
    calendarEnd.setDate(monthEnd.getDate() + (6 - monthEnd.getDay()));
    const dayCount = Math.round((calendarEnd.getTime() - calendarStart.getTime()) / 86400000) + 1;

    return Array.from({ length: dayCount }, (_, index) => {
      const date = new Date(calendarStart);
      date.setDate(calendarStart.getDate() + index);
      const dateIso = date.toISOString().slice(0, 10);
      const appointments = this.appointmentsForDate(dateIso);

      return {
        dateIso,
        weekday: date.toLocaleDateString(undefined, { weekday: 'short' }),
        day: date.toLocaleDateString(undefined, { day: 'numeric' }),
        month: date.toLocaleDateString(undefined, { month: 'short' }),
        inMonth: date.getMonth() === this.calendarViewDate.getMonth(),
        past: date < today,
        slots:
          appointments.length > 0
            ? appointments.map((appointment) => ({
                label: `${appointment.status}: ${this.calendarSlotLabel(appointment)}`,
                status: appointment.status === 'pending' ? 'pending' : 'booked',
                appointment,
              }))
            : [{ label: 'Open', status: 'open' }],
      };
    });
  }

  private appointmentsForDate(dateIso: string): Appointment[] {
    return this.appointments.filter((appointment) => appointment.preferredDate === dateIso || appointment.scheduledStart?.startsWith(dateIso));
  }

  protected slotFromScheduledTime(scheduledStart?: string): string | undefined {
    if (!scheduledStart) {
      return undefined;
    }

    const hour = new Date(scheduledStart).getHours();

    if (hour < 12) {
      return 'Morning';
    }

    if (hour < 17) {
      return 'Afternoon';
    }

    return 'Evening';
  }

  private calendarSlotLabel(appointment: Appointment): string {
    if (appointment.status === 'booked' && appointment.scheduledStart) {
      return new Date(appointment.scheduledStart).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    }

    return appointment.preferredTime || this.slotFromScheduledTime(appointment.scheduledStart) || appointment.service;
  }

  private refreshCalendarTitle(): void {
    this.calendarTitle = this.calendarViewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }

}
